/**
 * Employees list — a filtered view of `hr.Employment` rows, scoped to
 * currently-active employments by default. This is the operational HR view:
 * employee number, current role, department, manager, start date.
 *
 * Important: this is NOT a separate entity. Each row is an `hr.Employment`
 * whose underlying `hr.Person` is the unified identity. Clicking a row
 * navigates to the Person record page with the Employments tab pre-selected
 * so the same record-page UI serves both the People and Employees entry
 * points (one page to maintain, two operational lenses).
 *
 * Same person can therefore appear in three lists:
 *   - People       (because they're a Person)
 *   - Employees    (this list — because they have an active Employment)
 *   - Candidates   (because they have an `ats.CandidateProfile`)
 */

import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'

// ─────────────────── Types ───────────────────

type Employment = {
  id: number
  person: number
  person_name: string
  employee_number: string
  status: 'ACTIVE' | 'ON_LEAVE' | 'ENDED' | 'TERMINATED'
  employment_type: string
  position: number | null
  position_code: string | null
  position_title: string | null
  department: number | null
  department_name: string | null
  manager: number | null
  manager_name: string | null
  subsidiary: number | null
  subsidiary_name: string | null
  start_date: string | null
  end_date: string | null
}

type Department = { id: number; name: string; is_active: boolean }
type Subsidiary = { id: number; name: string; code: string }

const STATUS_FILTERS = ['ACTIVE', 'ALL', 'ENDED'] as const
type StatusFilter = typeof STATUS_FILTERS[number]

const STATUS_COLOR: Record<Employment['status'], string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  ON_LEAVE: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-gray-100 text-gray-700',
  TERMINATED: 'bg-red-100 text-red-700',
}

// ─────────────────── Page ───────────────────

export default function HREmployees() {
  const navigate = useNavigate()

  const empsQ = useQuery({
    queryKey: ['hr', 'employments'],
    queryFn: async () => (await api.get<Employment[]>('/hr/employments/')).data,
  })
  const depsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const subsQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
  })

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>('ACTIVE')
  const [deptFilter, setDeptFilter] = useState<string>('ALL')
  const [subFilter, setSubFilter] = useState<string>('ALL')

  const rows = (empsQ.data || []).filter((e) => {
    // Status — ACTIVE bucket includes ON_LEAVE (people are still on payroll);
    // ENDED covers both ENDED and TERMINATED for the recruiter UI's mental model.
    if (status === 'ACTIVE' && e.status !== 'ACTIVE' && e.status !== 'ON_LEAVE') return false
    if (status === 'ENDED' && e.status !== 'ENDED' && e.status !== 'TERMINATED') return false
    if (deptFilter !== 'ALL' && String(e.department) !== deptFilter) return false
    if (subFilter !== 'ALL' && String(e.subsidiary) !== subFilter) return false
    if (search) {
      const s = search.toLowerCase()
      const hay = [
        e.person_name, e.employee_number,
        e.position_title, e.position_code,
        e.department_name, e.manager_name,
      ].filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(s)) return false
    }
    return true
  })

  const departments = (depsQ.data || []).filter((d) => d.is_active)
  const subsidiaries = subsQ.data || []

  return (
    <AdminLayout>
      <div className="mb-4">
        <h2 className="text-2xl font-semibold text-foreground tracking-tight">Employees</h2>
        <p className="text-sm text-muted-foreground mt-1">
          People currently employed by the organization. Click a row to open the full Person record.
        </p>
      </div>

      <div className="bg-card rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
            Search
          </label>
          <Input
            placeholder="Name, employee #, position, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[140px]">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
            Status
          </label>
          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="ENDED">Ended / terminated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[180px]">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
            Department
          </label>
          <SearchableSelect
            value={deptFilter}
            onChange={setDeptFilter}
            placeholder="All departments"
            options={[
              { value: 'ALL', label: 'All departments' },
              ...departments.map((d) => ({ value: String(d.id), label: d.name })),
            ]}
          />
        </div>
        {subsidiaries.length > 0 && (
          <div className="min-w-[180px]">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
              Subsidiary
            </label>
            <SearchableSelect
              value={subFilter}
              onChange={setSubFilter}
              placeholder="All subsidiaries"
              options={[
                { value: 'ALL', label: 'All subsidiaries' },
                ...subsidiaries.map((s) => ({
                  value: String(s.id), label: s.name, hint: s.code,
                })),
              ]}
            />
          </div>
        )}
      </div>

      {empsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No employees match these filters.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="px-4 py-2 text-xs text-muted-foreground border-b">
            {rows.length} {rows.length === 1 ? 'employee' : 'employees'}
          </div>
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Employee #</Th>
                <Th>Name</Th>
                <Th>Position</Th>
                <Th>Department</Th>
                <Th>Manager</Th>
                <Th>Start</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((e) => (
                <tr
                  key={e.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() =>
                    navigate(`/admin/hr/people/${e.person}?tab=employments`)
                  }
                >
                  <Td className="font-mono text-xs">{e.employee_number || '—'}</Td>
                  <Td className="text-sm font-medium">{e.person_name}</Td>
                  <Td className="text-xs">
                    {e.position_title ? (
                      <>
                        <div className="font-mono text-muted-foreground">{e.position_code}</div>
                        <div>{e.position_title}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </Td>
                  <Td className="text-xs">{e.department_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{e.manager_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs whitespace-nowrap">
                    {e.start_date || <span className="text-muted-foreground">—</span>}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[e.status]}`}>
                      {e.status}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

// ─────────────────── Helpers ───────────────────

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
